import { ReactNode, useContext, useMemo } from 'react';

import { Tabs, Tab, Box, Badge } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Card from '@/components/card';
import { PostDetailsContext } from '@/contexts/PostDetailsContext';
import { getLogosFound, toLogoKeyword } from '@/utils/logos/logos';
import { processMediaWithKeywords } from '@/utils/ocr';
import { processOldOcrText } from '@/utils/ocr/ocr';

import PostChildren from './PostChildren';
import PostLogos from './PostLogos';
import PostOcr from './PostOcr';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && <Box paddingY={2}>{children}</Box>}
  </div>
);

const getTabStyles = (
  theme: any,
  selectedValue: number | null,
  tabValue: number
) => ({
  textTransform: 'none',
  fontWeight: selectedValue === tabValue ? 'bold' : 'normal',
  minWidth: '120px',
  '&:hover': {
    color: 'inherit',
    backgroundColor: theme.palette.grey[200]
  },
  '&.Mui-selected': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.grey[100]
  }
});

const PostVisuals = () => {
  const theme = useTheme();
  const {
    data,
    keywordList,
    activeIndex,
    selectedKeywords,
    hasMinorsResults,
    logos,
    hasLogosResults,
    isRecognitionHighlighted0to6,
    isRecognitionHighlighted7to14,
    isRecognitionHighlighted15to17,
    hasOcrAdvancedItems,
    hasOcrOldItems,
    resetMarkers,
    selectedVisualTab,
    setSelectedVisualTab
  } = useContext(PostDetailsContext);

  const getDefaultTab = () => {
    if (hasOcrAdvancedItems || (!hasMinorsResults && hasOcrOldItems)) return 1;
    if (hasLogosResults) return 2;
    return 0;
  };
  const value = selectedVisualTab ?? getDefaultTab();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedVisualTab(newValue);
    resetMarkers();
  };

  const modMedia = useMemo(
    () => processMediaWithKeywords(data?.media, selectedKeywords, keywordList),
    [data?.media, selectedKeywords, keywordList]
  );

  const isAdvancedOcrHighlighted = useMemo(
    () =>
      modMedia?.some((media) =>
        media.visualRecognition?.some(
          (recognition) =>
            recognition.type === 'ocr-adv' &&
            recognition.items.some(
              (item) =>
                ('textFound' in item &&
                  item.textFound.some((text) =>
                    text.content.includes('</b>')
                  )) ||
                ('content' in item &&
                  typeof item.content === 'string' &&
                  item.content.includes('</b>'))
            )
        )
      ),
    [modMedia]
  );

  const isOldOcrHighlighted = useMemo(
    () =>
      processOldOcrText(data?.media[activeIndex], selectedKeywords)?.includes(
        '</b>'
      ),
    [data, selectedKeywords, activeIndex]
  );

  const hasHighlightedLogos =
    hasLogosResults &&
    (logos[activeIndex] ?? []).some((obj) =>
      getLogosFound(obj).some((logo) =>
        selectedKeywords.includes(toLogoKeyword(logo.content.brand))
      )
    );

  const hasBorder =
    isRecognitionHighlighted0to6 ||
    isRecognitionHighlighted7to14 ||
    isRecognitionHighlighted15to17 ||
    isOldOcrHighlighted ||
    isAdvancedOcrHighlighted;

  const hasOcrResults = hasOcrAdvancedItems || hasOcrOldItems;

  if (!hasMinorsResults && !hasOcrResults && !hasLogosResults) return <></>;

  const invisibleBadgeText = !isAdvancedOcrHighlighted || value === 1;
  const invisibleBadgeLogos = !hasHighlightedLogos || value === 2;

  return (
    <Card
      sx={{ marginBottom: 2 }}
      hasBorder={hasBorder}
      isCollapsible
      title="Visuals"
      content={
        <>
          <Tabs
            value={value}
            onChange={handleChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTabs-indicator': {
                height: '4px'
              }
            }}
          >
            {hasMinorsResults && (
              <Tab
                label="Children"
                value={0}
                sx={getTabStyles(theme, value, 0)}
              />
            )}
            {hasOcrResults && (
              <Tab
                label={
                  <Badge
                    color="info"
                    variant="dot"
                    invisible={invisibleBadgeText}
                  >
                    Text on screen
                  </Badge>
                }
                value={1}
                sx={getTabStyles(theme, value, 1)}
              />
            )}
            {hasLogosResults && (
              <Tab
                label={
                  <Badge
                    color="info"
                    variant="dot"
                    invisible={invisibleBadgeLogos}
                  >
                    Logos
                  </Badge>
                }
                value={2}
                sx={getTabStyles(theme, value, 2)}
              />
            )}
          </Tabs>
          <TabPanel value={value} index={0}>
            <PostChildren />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <PostOcr />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <PostLogos />
          </TabPanel>
        </>
      }
    />
  );
};

export default PostVisuals;
